import React, { useState, useEffect } from "react";
import axios from "axios";
import { format, addDays } from "date-fns";
import { Calendar as CalendarIcon, Users, Clock, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { getAvailableTimeSlots, validateOperatingHours } from "@/utils/operatingHours";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inline styles to override dark theme for calendar on public page
const calendarStyles = `
  .public-calendar * {
    color: #000 !important;
  }
  .public-calendar .rdp {
    --rdp-cell-size: 40px !important;
    --rdp-accent-color: #16a34a !important;
    --rdp-background-color: #16a34a !important;
  }
  .public-calendar .rdp-day {
    color: #374151 !important;
    background: transparent !important;
  }
  .public-calendar .rdp-day_selected,
  .public-calendar .rdp-day_selected:hover {
    background-color: #16a34a !important;
    color: #fff !important;
  }
  .public-calendar .rdp-day:hover:not(.rdp-day_selected) {
    background-color: #f3f4f6 !important;
    color: #000 !important;
  }
  .public-calendar .rdp-caption_label {
    color: #111827 !important;
    font-weight: 600 !important;
  }
  .public-calendar .rdp-head_cell {
    color: #6b7280 !important;
  }
  .public-calendar .rdp-nav_button {
    color: #374151 !important;
  }
  .public-calendar .rdp-nav_button:hover {
    background-color: #f3f4f6 !important;
  }
  .public-calendar .rdp-day_disabled {
    color: #d1d5db !important;
    opacity: 0.5 !important;
  }
`;

const PublicBooking = () => {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [bookingData, setBookingData] = useState({
    party_size: 2,
    date: "",
    time: "",
    customer_name: "",
    phone: "",
    email: "",
    notes: ""
  });

  // Get available time slots based on selected date
  const [timeSlots, setTimeSlots] = useState([]);

  useEffect(() => {
    if (bookingData.date) {
      const slots = getAvailableTimeSlots(bookingData.date);
      setTimeSlots(slots);
    }
  }, [bookingData.date]);

  const handleSubmit = async () => {
    if (!bookingData.customer_name || !bookingData.phone || !bookingData.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate operating hours
    const validation = validateOperatingHours(bookingData.date, bookingData.time);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    try {
      const bookingRef = `PUB${Date.now().toString().slice(-6)}`;
      console.log("Creating reservation with data:", { booking_ref: bookingRef, ...bookingData });
      console.log("API endpoint:", `${API}/webhook/new-reservation`);
      
      const response = await axios.post(`${API}/webhook/new-reservation`, {
        booking_ref: bookingRef,
        ...bookingData,
        status: "confirmed"
      }, { headers: { "X-Webhook-Secret": "bdc-webhook-2026-secure" } });
      
      console.log("Reservation created successfully:", response.data);
      setSubmitted(true);
    } catch (error) {
      console.error("Error creating reservation:", error);
      console.error("Error details:", error.response?.data);
      
      if (error.code === 'ERR_NETWORK') {
        toast.error("Cannot connect to server. Please check your internet connection.");
      } else if (error.response?.status === 404) {
        toast.error("Booking service not found. Please contact support.");
      } else if (error.response?.status >= 500) {
        toast.error("Server error. Please try again later.");
      } else {
        toast.error("Failed to create reservation. Please try again.");
      }
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Reservation Confirmed!</h1>
          <p className="text-sm text-gray-600 mb-4">
            Thank you, {bookingData.customer_name}! Your table for {bookingData.party_size} guests is reserved.
          </p>
          <div className="bg-slate-50 rounded-lg p-3 mb-4 text-left">
            <p className="text-xs text-gray-500 mb-1">Reservation Details:</p>
            <p className="text-sm font-semibold text-gray-900">{bookingData.date && format(new Date(bookingData.date), "MMMM d, yyyy")}</p>
            <p className="text-sm font-semibold text-gray-900">{bookingData.time}</p>
            <p className="text-sm text-gray-600 mt-1">{bookingData.party_size} guests</p>
          </div>
          <p className="text-xs text-gray-500">
            A confirmation has been sent to {bookingData.email}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{calendarStyles}</style>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-4">
        <div className="container mx-auto px-4 max-w-xl">
        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Buffet des Continents</h1>
          <p className="text-sm text-gray-600">Reserve your table in minutes</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-4">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= s ? "bg-green-600 text-white" : "bg-gray-200 text-gray-600"
              }`}>
                {s}
              </div>
              {s < 4 && <div className={`flex-1 h-1 mx-1 ${step > s ? "bg-green-600" : "bg-gray-200"}`}></div>}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Step 1: Party Size */}
          {step === 1 && (
            <div data-testid="step-party-size">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-bold text-gray-900">How many guests?</h2>
              </div>
              <div className="grid grid-cols-6 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 25, 30, 35, 40, 45, 50].map((size) => (
                  <button
                    key={size}
                    data-testid={`party-size-${size}`}
                    onClick={() => setBookingData({ ...bookingData, party_size: size })}
                    className={`py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                      bookingData.party_size === size
                        ? "bg-green-600 text-white shadow-lg scale-105"
                        : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <Button
                onClick={() => setStep(2)}
                className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white py-4"
              >
                Continue
              </Button>
            </div>
          )}

          {/* Step 2: Date */}
          {step === 2 && (
            <div data-testid="step-date">
              <div className="flex items-center gap-2 mb-4">
                <CalendarIcon className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-bold text-gray-900">Select Date</h2>
              </div>
              <div className="flex justify-center bg-white p-4 rounded-lg public-calendar">
                <Calendar
                  mode="single"
                  selected={bookingData.date ? new Date(bookingData.date + 'T12:00:00') : undefined}
                  onSelect={(date) => {
                    if (date) {
                      // Format in local timezone to avoid date shift
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      setBookingData({ ...bookingData, date: `${year}-${month}-${day}` });
                    }
                  }}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const maxDate = addDays(today, 30);
                    return date < today || date > maxDate;
                  }}
                  initialFocus
                />
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => setStep(1)} variant="outline" className="flex-1 text-gray-700 border-gray-300 hover:bg-gray-100">Back</Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!bookingData.date}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Time */}
          {step === 3 && (
            <div data-testid="step-time">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-bold text-gray-900">Select Time</h2>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {timeSlots.map((time) => (
                  <button
                    key={time}
                    data-testid={`time-${time}`}
                    onClick={() => setBookingData({ ...bookingData, time: time })}
                    className={`py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                      bookingData.time === time
                        ? "bg-green-600 text-white shadow-lg scale-105"
                        : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => setStep(2)} variant="outline" className="flex-1 text-gray-700 border-gray-300 hover:bg-gray-100">Back</Button>
                <Button
                  onClick={() => setStep(4)}
                  disabled={!bookingData.time}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Details */}
          {step === 4 && (
            <div data-testid="step-details">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Your Details</h2>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="name" className="text-gray-700">Full Name *</Label>
                  <Input
                    id="name"
                    data-testid="booking-name-input"
                    value={bookingData.customer_name}
                    onChange={(e) => setBookingData({ ...bookingData, customer_name: e.target.value })}
                    className="mt-1"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-gray-700">Phone Number *</Label>
                  <Input
                    id="phone"
                    data-testid="booking-phone-input"
                    value={bookingData.phone}
                    onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                    className="mt-1"
                    placeholder="+1 (514) 999-9999"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-700">Email Address *</Label>
                  <Input
                    id="email"
                    data-testid="booking-email-input"
                    type="email"
                    value={bookingData.email}
                    onChange={(e) => setBookingData({ ...bookingData, email: e.target.value })}
                    className="mt-1"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="notes" className="text-gray-700">Special Requests (Optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="booking-notes-input"
                    value={bookingData.notes}
                    onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                    className="mt-1"
                    rows={3}
                    placeholder="Any special requests, dietary restrictions, or occasions?"
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="mt-3 bg-slate-50 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-900 mb-1">Reservation Summary:</p>
                <p className="text-sm text-gray-700">📅 {bookingData.date && format(new Date(bookingData.date), "MMMM d, yyyy")}</p>
                <p className="text-sm text-gray-700">🕐 {bookingData.time}</p>
                <p className="text-sm text-gray-700">👥 {bookingData.party_size} guests</p>
              </div>

              <div className="flex gap-2 mt-4">
                <Button onClick={() => setStep(3)} variant="outline" className="flex-1 text-gray-700 border-gray-300 hover:bg-gray-100">Back</Button>
                <Button
                  data-testid="confirm-booking-button"
                  onClick={handleSubmit}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-4"
                >
                  Confirm Reservation
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-8">
          By making a reservation, you agree to our cancellation policy
        </p>
        </div>
      </div>
    </>
  );
};

export default PublicBooking;
